import { Handle, Position } from '@xyflow/react'
import { CircleHelp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { GraphNodeData } from '@/types/graph'
import { cn } from '@/utils/cn'

type Question = { type?: string; instructions?: string; criteria?: Record<string, string> | string[]; min_confidence?: number }

export default function QuestionNode({ data, selected }: { data: GraphNodeData; selected?: boolean }) {
  const { t } = useTranslation()
  const questions = (data.config?.questions ?? {}) as Record<string, Question>
  return (
    <div className={cn('min-w-48 max-w-72 rounded-lg border-2 border-primary/30 bg-background p-3 text-foreground', selected && 'border-primary ring-2 ring-primary/30')} data-testid="workflow-question-node">
      <Handle type="target" position={Position.Top} />
      <div className="mb-2 flex items-center gap-2 text-sm font-bold"><CircleHelp size={16} className="text-accent-foreground" aria-hidden="true" />{t('build.questionNode')} · {data.label}</div>
      {Object.entries(questions).slice(0, 3).map(([id, question]) => (
        <div key={id} className="mt-2 border-t border-border pt-2 text-xs">
          <div className="font-medium">{id} · {question.type}</div>
          <p className="text-muted-foreground">{question.instructions}</p>
          {question.criteria && <ul aria-label={t('build.questionCriteria')} className="mt-1 list-inside list-disc">
            {Object.entries(question.criteria).slice(0, 4).map(([key, value]) => <li key={key}>{Array.isArray(question.criteria) ? value : `${key}: ${value}`}</li>)}
          </ul>}
          {question.criteria && Object.keys(question.criteria).length > 4 && <p>{t('build.questionMore', { count: Object.keys(question.criteria).length - 4 })}</p>}
          {typeof question.min_confidence === 'number' && <p className="mt-1 text-muted-foreground">{t('build.questionConfidence')}: {question.min_confidence}</p>}
        </div>
      ))}
      {Object.keys(questions).length > 3 && <p className="mt-2 text-xs">{t('build.questionMore', { count: Object.keys(questions).length - 3 })}</p>}
      {data.config?.output_key && <p className="mt-2 font-mono text-xs text-accent-foreground">→ {String(data.config.output_key)}</p>}
      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
